import { useCallback, useMemo, useState } from "react";

import { cloneDeep, orderBy, sortBy } from "lodash";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { PREVIEW_LABEL_RULE_MATCHES } from "@gql/label-rules-v3";
import { useFeatureFlags, useNorthbeamLazyQuery } from "@hooks/http";
import {
  type PreviewLabelRuleMatches,
  type PreviewLabelRuleMatchesVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";
import { Paginator } from "@shared/paginator";
import Spinner from "@shared/spinner";
import { Switch } from "@shared/switch";
import { TextInput } from "@shared/text-input";

import { type AdvancedSegmentDescriptor, type Sorting } from "../types";

import { LabelRuleSimulatorTable } from "./label-rule-simulator-table";
import { SearchMatcherEditor } from "./search-matcher-editor";

const PER_PAGE = 20;

interface Props {
  segments: AdvancedSegmentDescriptor[];
  segment: AdvancedSegmentDescriptor;
}

export const LabelRuleSimulator = ({ segments, segment }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { hasFeatureFlag } = useFeatureFlags();
  const [sorting, setSorting] = useState<Sorting>({
    column: "priorityOrderedMatchedRules",
    order: "desc",
  });
  const [page, setPage] = useState(1);
  const [isMatcherSearch, setIsMatcherSearch] = useState(false);
  const [search, setSearch] = useState("");
  const [matcherToSearch, setMatcherToSearch] = useState<any>(null);
  const [simulatedSegment, setSimulatedSegment] = useState(segment);

  const {
    loadData: simulate,
    data: { data, loading, error },
  } = useNorthbeamLazyQuery<
    PreviewLabelRuleMatches,
    PreviewLabelRuleMatchesVariables
  >(PREVIEW_LABEL_RULE_MATCHES);

  const handleClick = useCallback(() => {
    const labelRules = [];

    segments.forEach((e) => {
      if (e.id === segment.id) {
        return;
      }

      if (!e.active) {
        return;
      }

      if (e.level !== segment.level) {
        return;
      }

      labelRules.push({
        active: true,
        createdAt: "2000-01-01T00:00:00Z",
        name: e.name,
        output: e.output.trim(),
        priority: e.priority,
        matcher: e.matcher,
      });
    });

    labelRules.push({
      active: true,
      createdAt: "2000-01-01T00:00:00Z",
      name: segment.name,
      output: segment.output.trim(),
      priority: segment.priority,
      matcher: segment.matcher,
    });

    setSimulatedSegment(segment);

    simulate({
      variables: {
        level: segment.level,
        search: search ? search : undefined,
        matcherSearch: matcherToSearch,
        labelRules: sortBy(labelRules, "priority"),
      },
    });
  }, [simulate, segments, segment, search, matcherToSearch]);

  const rows = useMemo(() => {
    if (!data) {
      return [];
    }

    const result = data.me.previewLabelRuleMatches.map((row) => {
      return {
        visitsLast30d: row.visitsLast30d,
        name: row.name,
        identificationMethod: row.identificationMethod,
        matchResult: row.matchResult,
        metadata: row.metadata,
      };
    });

    return orderBy(
      result,
      [
        sorting.column === "priorityOrderedMatchedRules"
          ? (row) => {
              const matchResult = row.matchResult;
              const firstMatch = matchResult.priorityOrderedMatchedRules[0];
              const isCurrentRule =
                firstMatch &&
                firstMatch.name === simulatedSegment.name &&
                firstMatch.priority === simulatedSegment.priority;

              return isCurrentRule ? 1 : 0;
            }
          : sorting.column,
        "visitsLast30d",
      ],
      [sorting.order, "desc"],
    );
  }, [data, sorting, simulatedSegment]);

  const visibleRows = rows.slice(
    (page - 1) * PER_PAGE,
    (page - 1) * PER_PAGE + PER_PAGE,
  );

  return (
    <>
      <h4 className="m-0 text-xl font-bold mb-2">Simulation</h4>
      <div className="text-sm mt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <PrimaryButton
              disabled={loading}
              className="font-bold flex items-center gap-2"
              onClick={handleClick}
            >
              {loading && <Spinner colorClassname="text-white-400" />}
              SIMULATE
            </PrimaryButton>
            {!isMatcherSearch && (
              <TextInput
                className="shrink-0"
                inputClassName="pl-8"
                width="300px"
                placeholder="Search name..."
                leftIcon={
                  <i className="fa-regular fa-sharp fa-search text-gray text-sm" />
                }
                value={search}
                onChange={(e) => {
                  prettyLogEventPage({
                    domain: "Breakdown v2",
                    action: "onChange",
                    additionalActionInfo: "Search matches",
                  });

                  setSearch(e.target.value);
                }}
              />
            )}
            {hasFeatureFlag("enableBreakdownV2AdvancedSearch") && (
              <label className="flex items-center gap-2 m-0 cursor-pointer">
                <Switch
                  size="small"
                  value={isMatcherSearch}
                  onChange={(value) => {
                    if (value) {
                      setSearch("");
                      setMatcherToSearch(cloneDeep(segment.matcher));
                    } else {
                      setSearch("");
                      setMatcherToSearch(null);
                    }

                    setIsMatcherSearch(value);
                  }}
                />
                {isMatcherSearch
                  ? "Switch to simple search"
                  : "Switch to rule based search"}
              </label>
            )}
          </div>
          {rows.length > 0 && (
            <Paginator
              page={page}
              perPage={PER_PAGE}
              total={rows.length}
              onChange={(value) => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo: "Change preview matches page",
                  data: {
                    newPage: value,
                    pageContext: { page },
                  },
                });

                setPage(value);
              }}
            />
          )}
        </div>
        {isMatcherSearch && (
          <div className="mt-3">
            <SearchMatcherEditor
              mainMatcher={segment.matcher}
              searchMatcher={matcherToSearch}
              onChange={setMatcherToSearch}
            />
          </div>
        )}
        <div className="border border-gray rounded-lg overflow-auto mt-4 h-[400px] overflow-auto">
          <LabelRuleSimulatorTable
            rows={visibleRows}
            simulatedSegment={simulatedSegment}
            isDataPresent={!!data}
            loading={loading}
            error={error}
            sorting={sorting}
            onChangeSorting={(value) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Change preview matches sorting",
                data: {
                  newSorting: value,
                  pageContext: { sorting },
                },
              });

              setSorting(value);
            }}
          />
        </div>
      </div>
    </>
  );
};
